import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, Drawer, Menu, Typography } from '@mui/material';
import {
  CalendarMonthOutlined as BillIcon,
  ChevronRight as ChevronIcon,
  EditOutlined as FormIcon,
  TrackChangesOutlined as GoalIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FinMark from '../brand/FinMark';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { BOT_URL } from '../../config/telegram';
import ConnectTelegramDialog, { useConnectTelegramFor } from '../contexts/ConnectTelegramDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useFinChannel } from '../../hooks/useFinChannel';
import { tokens } from '../../theme/tokens';

interface Props {
  /** desktop: the button the menu hangs from; mobile: ignored */
  anchor: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  asSheet: boolean;
}

interface RowContent {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  primary?: boolean;
}

const rowSx = (primary: boolean) => ({
  width: '100%',
  p: 1.75,
  gap: 1.75,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'left' as const,
  borderRadius: '18px',
  border: primary ? 'none' : `1px solid ${tokens.line}`,
  bgcolor: primary ? tokens.accent : tokens.surface,
  color: primary ? '#FFFFFF' : tokens.ink,
  font: 'inherit',
  textDecoration: 'none',
});

const RowBody: React.FC<RowContent & { chevron: boolean }> = ({ icon, title, hint, primary = false, chevron }) => (
  <>
    <Box
      component='span'
      sx={{
        width: 46,
        height: 46,
        flexShrink: 0,
        borderRadius: '14px',
        bgcolor: primary ? '#FFFFFF' : tokens.bg,
        color: tokens.ink2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box component='strong' sx={{ fontSize: 16 }}>{title}</Box>
      {hint && (
        <Box component='span' sx={{ fontSize: 13, lineHeight: 1.4, color: primary ? 'rgba(255,255,255,.88)' : tokens.ink3 }}>
          {hint}
        </Box>
      )}
    </Box>
    {chevron && <ChevronIcon sx={{ color: primary ? '#FFFFFF' : tokens.ink3 }} />}
  </>
);

const Row: React.FC<RowContent & { onClick?: () => void; href?: string }> = ({ onClick, href, ...content }) => (
  <ButtonBase
    {...(href ? { component: 'a' as const, href, target: '_blank', rel: 'noopener noreferrer' } : {})}
    onClick={onClick}
    sx={rowSx(Boolean(content.primary))}
  >
    <RowBody {...content} chevron />
  </ButtonBase>
);

/** The same card with nothing to press: an instruction, not an action. */
const NoteRow: React.FC<RowContent> = (content) => (
  <Box sx={{ ...rowSx(false), bgcolor: tokens.accentSoft, border: 'none' }}>
    <RowBody {...content} chevron={false} />
  </Box>
);

/**
 * "Registrar": the Fin first (it does the work), then the forms. It says
 * where the record goes, the personal view or the group in focus. A group is
 * reached through its own Telegram group: the private chat would record the
 * expense into the personal view instead.
 */
const RegisterActions: React.FC<Props> = ({ anchor, open, onClose, asSheet }) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { selectedContext } = useFinancialContexts();
  // "Adicionar o Fin": the same window as on Grupos, which waits for Fin and offers the invite.
  const { openDialog, dialogProps } = useConnectTelegramFor(selectedContext);
  const channel = useFinChannel();
  const { state: authState } = useAuth();
  const linked = Boolean(authState.user?.isTelegramLinked);
  const where = selectedContext && selectedContext.type !== 'personal' ? selectedContext.name : t('shell.personal');

  const go = (path: string, state: Record<string, unknown>) => {
    onClose();
    navigate(path, { state });
  };

  const finIcon = <FinMark size={30} />;
  const finRow = (() => {
    // An unlinked Telegram account would record into a different account: link it first.
    if (!linked) {
      return <Row primary onClick={() => go('/settings/telegram', {})} icon={finIcon} title={t('shell.sendToFin')} hint={t('shell.sendToFinConnectHint')} />;
    }
    switch (channel.kind) {
      case 'personal':
        return <Row primary href={BOT_URL} onClick={onClose} icon={finIcon} title={t('shell.sendToFin')} hint={t('shell.sendToFinHint')} />;
      case 'groupChat': {
        const where = channel.chatTitle
          ? t('shell.sendInGroupHint', { chat: channel.chatTitle })
          : t('shell.sendInGroupHintUntitled', { name: channel.groupName });
        // Opens the group's own chat when Telegram allows a link to it.
        if (channel.url) {
          return <Row primary href={channel.url} onClick={onClose} icon={finIcon} title={t('shell.openGroup')} hint={where} />;
        }
        return (
          <NoteRow
            icon={finIcon}
            title={t('shell.sendInGroup')}
            hint={channel.needsAdmin ? `${where} ${t('shell.makeFinAdmin')}` : where}
          />
        );
      }
      default:
        return channel.canAddFin ? (
          <Row
            primary
            onClick={() => {
              onClose();
              openDialog();
            }}
            icon={finIcon}
            title={t('shell.addFinToGroup')}
            hint={t('shell.addFinToGroupHint', { name: channel.groupName })}
          />
        ) : (
          <NoteRow icon={finIcon} title={t('shell.groupNoChat')} hint={t('shell.groupNoChatHint', { name: channel.groupName })} />
        );
    }
  })();

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, px: 0.5 }}>
        <Typography component='h2' sx={{ fontFamily: tokens.display, fontWeight: 700, fontSize: 19 }}>
          {t('shell.register')}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink2 }}>{t('shell.registerIn', { name: where })}</Typography>
      </Box>
      {finRow}
      <Row icon={<FormIcon />} title={t('shell.fillHere')} hint={t('shell.fillHereHint')} onClick={() => go('/transactions', { newTransaction: true })} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
        <ButtonBase
          onClick={() => go('/planning', { open: 'bill' })}
          sx={{ p: 1.75, gap: 1, flexDirection: 'column', alignItems: 'flex-start', borderRadius: '16px', border: `1px solid ${tokens.line}`, font: 'inherit', color: tokens.ink }}
        >
          <BillIcon sx={{ color: tokens.ink2 }} />
          <Box component='strong' sx={{ fontSize: 14 }}>{t('shell.newBill')}</Box>
        </ButtonBase>
        <ButtonBase
          onClick={() => go('/planning', { open: 'goal' })}
          sx={{ p: 1.75, gap: 1, flexDirection: 'column', alignItems: 'flex-start', borderRadius: '16px', border: `1px solid ${tokens.line}`, font: 'inherit', color: tokens.ink }}
        >
          <GoalIcon sx={{ color: tokens.ink2 }} />
          <Box component='strong' sx={{ fontSize: 14 }}>{t('shell.saveToGoal')}</Box>
        </ButtonBase>
      </Box>
      {asSheet && (
        <Button onClick={onClose} sx={{ mt: 0.5, color: tokens.ink2 }}>
          {t('shell.cancel')}
        </Button>
      )}
    </Box>
  );

  const connectDialog = (
    <ConnectTelegramDialog {...dialogProps} />
  );

  if (asSheet) {
    return (
      <>
      <Drawer anchor='bottom' open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '24px 24px 0 0', px: 2, pt: 1.25, pb: 3 } }}>
        <Box aria-hidden='true' sx={{ alignSelf: 'center', width: 40, height: 5, borderRadius: 99, bgcolor: tokens.line, mb: 1.5 }} />
        {content}
      </Drawer>
      {connectDialog}
      </>
    );
  }
  return (
    <>
    <Menu
      anchorEl={anchor}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      MenuListProps={{ sx: { p: 1.75, width: 380 } }}
    >
      {content}
    </Menu>
    {connectDialog}
    </>
  );
};

export default RegisterActions;
